import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

const STATUS_STYLES = {
  paid: "text-emerald-400 border-emerald-400/40 bg-emerald-400/10",
  pending: "text-amber-400 border-amber-400/40 bg-amber-400/10",
  failed: "text-red-400 border-red-400/40 bg-red-400/10",
  expired: "text-slate-400 border-[#263342]",
  refunded: "text-sky-400 border-sky-400/40 bg-sky-400/10",
};

export default function OrdersPanel() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stripeConfig, setStripeConfig] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: ordersData }, { data: configData }] = await Promise.all([
        api.get("/orders"),
        api.get("/payments/config"),
      ]);
      setOrders(ordersData);
      setStripeConfig(configData);
    } catch (err) {
      toast.error("Could not load orders", { description: formatApiError(err) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const paidTotal = orders.filter((o) => o.payment_status === "paid").reduce((sum, o) => sum + (o.amount || 0), 0);

  return (
    <div className="mt-12" data-testid="orders-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display font-bold uppercase tracking-tight text-2xl text-slate-100">Part Orders</h2>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mt-1">
            Online Stripe payments — paid total: ${(paidTotal / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {stripeConfig && (
            <span
              data-testid="stripe-mode-chip"
              className={`font-mono text-[10px] uppercase tracking-[0.15em] border px-3 py-2 ${
                stripeConfig.mode === "live"
                  ? "text-emerald-400 border-emerald-400/40 bg-emerald-400/10"
                  : "text-amber-400 border-amber-400/40 bg-amber-400/10"
              }`}
            >
              Stripe: {stripeConfig.mode === "live" ? "Live" : "Test"} mode{stripeConfig.own_key ? " · your key" : " · platform key"}
            </span>
          )}
          <a
            href="https://dashboard.stripe.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="stripe-dashboard-link"
            className="font-mono text-xs uppercase tracking-[0.15em] border border-[#263342] px-4 py-2.5 text-slate-300 hover:border-crimson hover:text-crimson transition-colors"
          >
            Open Stripe Dashboard →
          </a>
          <button data-testid="orders-refresh-button" onClick={load} className="text-slate-400 hover:text-crimson transition-colors" aria-label="Refresh orders">
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      <div className="bg-[#0D1117] border border-[#263342] overflow-x-auto" data-testid="orders-table">
        {loading ? (
          <p className="p-10 text-center font-mono text-sm text-slate-500">Loading orders...</p>
        ) : orders.length === 0 ? (
          <p className="p-10 text-center font-mono text-sm text-slate-500" data-testid="orders-empty">No orders yet. Paid part purchases will appear here.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#263342] text-left">
                {["Date", "Part", "Customer", "Amount", "Status"].map((h) => (
                  <th key={h} className="px-5 py-4 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orders.map((o, i) => (
                <tr key={o.session_id} className="border-b border-[#263342]/60" data-testid={`order-row-${i}`}>
                  <td className="px-5 py-4 font-mono text-xs text-slate-400 whitespace-nowrap">
                    {new Date(o.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}{" "}
                    {new Date(o.created_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                  </td>
                  <td className="px-5 py-4 text-slate-100 font-semibold">{o.part_name}</td>
                  <td className="px-5 py-4 text-slate-400">{o.customer_email || "—"}</td>
                  <td className="px-5 py-4 font-display font-bold text-crimson" data-testid={`order-amount-${i}`}>
                    ${((o.amount || 0) / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-5 py-4">
                    <span className={`font-mono text-[10px] uppercase tracking-[0.15em] border px-2.5 py-1 ${STATUS_STYLES[o.payment_status] || STATUS_STYLES.expired}`} data-testid={`order-status-${i}`}>
                      {o.payment_status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
